// Angular
import { Component, inject } from '@angular/core';
import { Validators, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { finalize } from 'rxjs/operators';

// Prime NG
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

// Services
import { AuthenticationService } from 'src/app/@core/authentication/services/authentication.service';
import { LoginResponse } from 'src/app/@core/authentication/services/mfa.service';
import { Logger } from 'src/app/@core/services/logger/logger.service';

// Composants
import { FormBaseComponent } from 'src/app/@shared/components/abstract/form-base.component';
import { MfaVerifyFormComponent } from 'src/app/@shared/components/mfa/mfa-verify-form/mfa-verify-form.component';
import { MfaSetupFormComponent } from 'src/app/@shared/components/mfa/mfa-setup-form/mfa-setup-form.component';

const LOGGER = new Logger('LoginFormComponent');

type LoginView = 'login' | 'mfa-verify' | 'mfa-setup';

@Component({
    selector: 'app-login-form',
    imports: [
        ReactiveFormsModule,
        RouterModule,
        ButtonModule,
        InputTextModule,
        PasswordModule,
        ToggleSwitchModule,
        MfaVerifyFormComponent,
        MfaSetupFormComponent,
    ],
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss']
})
export class LoginFormComponent extends FormBaseComponent {
    private _authenticationService = inject(AuthenticationService);

    public view: LoginView = 'login';
    public preAuthToken: string = '';

    protected override buildForm() {
        this.form = this.formBuilder.group({
            email: ['', Validators.required],
            password: ['', Validators.required],
            remember: true
        });
    }

    public login(): void {
        this.loading = true;
        this._authenticationService.login$(this.form.value)
            .pipe(
                finalize(() => {
                    this.form.markAsPristine();
                    this.loading = false;
                })
            )
            .subscribe({
                next: (response: LoginResponse) => {
                    if (response.type === 'success') {
                        this._navigateAfterLogin();
                    } else if (response.type === 'mfa_required') {
                        this.preAuthToken = response.preAuthToken;
                        this.view = 'mfa-verify';
                    } else if (response.type === 'mfa_setup_required') {
                        this.preAuthToken = response.preAuthToken;
                        this.view = 'mfa-setup';
                    }
                },
                error: (error: any) => {
                    LOGGER.info(`Erreur de connexion: ${error}`);
                },
            });
    }

    public onMfaSuccess(): void {
        this._navigateAfterLogin();
    }

    public onSessionExpired(): void {
        this.preAuthToken = '';
        this.view = 'login';
        this.messageService.sendMessage({
            severity: 'warn',
            summary: 'Session expirée',
            detail: 'Votre session a expiré, veuillez vous reconnecter.',
            life: 8000
        });
    }

    private _navigateAfterLogin(): void {
        // `redirect` est une URL complète, posée par `AuthenticationGuard` : elle porte
        // le dossier et peut contenir des paramètres de requête (filtres de liste).
        // `navigateByUrl` la rejoue telle quelle, là où `navigate([url])` encoderait le
        // « ? » en segment de chemin. À défaut, on passe par la racine, qui rouvre le
        // dernier dossier travaillé (#173).
        const redirect: string = this.route.snapshot.queryParams['redirect'] || '/';
        this.router.navigateByUrl(redirect, { replaceUrl: true });
        this.userService.loadUserData();
    }
}
